import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Chip, Icon } from '@column/column-ui-kit';

import { CopyRow } from '../Transfers/_components/CopyRow';
import { ROUTE } from '~/app/routes';
import { CopyInput, SectionHeader } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { AchRepository, AchReturn, returnCodesTooltipMap } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Card, EditToolbar, Flex, FormElement, FormLabel, Grid, Inner, Text } from '~/styles';

interface Params {
  achTransferId: string;
}

const List = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-self: end;
  justify-self: start;
`;

export const PageReturnsView: React.FC = () => {
  const currentUser = useSessionStore((state) => state.currentUser);
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);
  const navigate = useNavigate();
  const { achTransferId } = useParams<keyof Params>() as Params;
  const [achReturn, setAchReturn] = useState<DeepPartial<AchReturn>>({});

  const handlePromiseError = (promise: any, callback: (response: any) => void) => {
    return promise
      .then((response: any) => {
        callback(response);
      })
      .catch((error: any) => {
        navigate(ROUTE.RETURNS);
        addDangerNotification({
          content: error.message,
        });
      });
  };

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    if (achTransferId) {
      handlePromiseError(AchRepository.getReturn(achTransferId), (response) => {
        setAchReturn(response);
      });
    }
  }, [achTransferId]);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => ({
          isSandbox: state.isSandbox,
          isLoading: state.isLoading,
          defaultPlatformId: state.currentUser?.defaultPlatformId,
        }),
        () => {
          if (!currentUser) {
            return;
          }
          if (achTransferId) {
            handlePromiseError(AchRepository.getReturn(achTransferId), setAchReturn);
          }
        }
      ),
    []
  );

  return (
    <>
      <SectionHeader text="Information" />
      <Inner pt={16}>
        <Grid>
          <FormElement>
            <FormLabel>ACH Transfer ID</FormLabel>
            <CopyInput value={achTransferId} />
          </FormElement>
          <List>
            <Button
              variant="secondary"
              onClick={() => navigate(`${ROUTE.TRANSFERS}/edit/ach/${achTransferId}`)}
              icon={<Icon.ArrowRight />}
            >
              View ACH Transfer
            </Button>
          </List>
        </Grid>
      </Inner>
      {achReturn?.details && achReturn.details.length > 0 && (
        <>
          <SectionHeader text="Return Information" />
          {achReturn.details.map((detail, index) => (
            <Inner key={index} pt={16}>
              <Card variant="secondary">
                {detail.returnCode && (
                  <CopyRow
                    label="Return Code"
                    value={
                      <Inner p={16}>
                        <Flex flexDirection="column" gap={8}>
                          <Chip size="large" type="danger">
                            {detail.returnCode}
                          </Chip>
                          {returnCodesTooltipMap.get(detail.returnCode) && (
                            <Text size={12}>{returnCodesTooltipMap.get(detail.returnCode)}</Text>
                          )}
                        </Flex>
                      </Inner>
                    }
                  />
                )}
                {detail.description && <CopyRow label="Return Description" value={detail.description || ''} />}
                {detail.addenda && <CopyRow label="Return Addenda" value={detail.addenda || ''} />}
                {detail.createdAt && (
                  <CopyRow label="Return Date" value={new Date(detail.createdAt).toLocaleString()} />
                )}
                {detail.status && <CopyRow label="Return Status" value={detail.status} />}
              </Card>
            </Inner>
          ))}
        </>
      )}
      <Inner>
        <EditToolbar>
          <Button onClick={() => navigate(-1)} variant="secondary" size="small">
            Back
          </Button>
        </EditToolbar>
      </Inner>
    </>
  );
};
